import React, { useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { supabase } from '../../../lib/supabase';
import NoiRockLogo from '../../../components/NoiRockLogo';
import { useTenant } from '../../../contexts/TenantContext';
import { useFeature } from '../../../hooks/useFeature';
import { FEATURE_GROUPS, FEATURES_BY_GROUP } from '../../../lib/featureCatalog';

const Icon = {
  dashboard: <svg width="18" height="18" viewBox="0 0 18 18" fill="none"><rect x="1" y="1" width="7" height="7" rx="1.5" stroke="currentColor" strokeWidth="1.5"/><rect x="10" y="1" width="7" height="7" rx="1.5" stroke="currentColor" strokeWidth="1.5"/><rect x="1" y="10" width="7" height="7" rx="1.5" stroke="currentColor" strokeWidth="1.5"/><rect x="10" y="10" width="7" height="7" rx="1.5" stroke="currentColor" strokeWidth="1.5"/></svg>,
  performance: <svg width="18" height="18" viewBox="0 0 18 18" fill="none"><circle cx="9" cy="9" r="7" stroke="currentColor" strokeWidth="1.5"/><path d="M9 5v4l3 2" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>,
  creators: <svg width="18" height="18" viewBox="0 0 18 18" fill="none"><circle cx="9" cy="6" r="3.5" stroke="currentColor" strokeWidth="1.5"/><path d="M2 16c0-3.314 3.134-6 7-6s7 2.686 7 6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/></svg>,
  cashback: <svg width="18" height="18" viewBox="0 0 18 18" fill="none"><circle cx="9" cy="9" r="7" stroke="currentColor" strokeWidth="1.5"/><path d="M6 13l6-8" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round"/><path d="M6.5 7.5C6.5 6.672 7.172 6 8 6h2a1.5 1.5 0 010 3H8a1.5 1.5 0 000 3h2a1.5 1.5 0 000-3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/></svg>,
  managers: <svg width="18" height="18" viewBox="0 0 18 18" fill="none"><circle cx="9" cy="5.5" r="3" stroke="currentColor" strokeWidth="1.5"/><path d="M2 16c0-3.314 3.134-6 7-6s7 2.686 7 6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/></svg>,
  groups: <svg width="18" height="18" viewBox="0 0 18 18" fill="none"><rect x="1" y="1" width="7" height="5" rx="1.5" stroke="currentColor" strokeWidth="1.5"/><rect x="10" y="1" width="7" height="5" rx="1.5" stroke="currentColor" strokeWidth="1.5"/><rect x="1" y="12" width="7" height="5" rx="1.5" stroke="currentColor" strokeWidth="1.5"/><rect x="10" y="12" width="7" height="5" rx="1.5" stroke="currentColor" strokeWidth="1.5"/></svg>,
  objectives: <svg width="18" height="18" viewBox="0 0 18 18" fill="none"><circle cx="9" cy="9" r="7" stroke="currentColor" strokeWidth="1.5"/><circle cx="9" cy="9" r="3.5" stroke="currentColor" strokeWidth="1.5"/><circle cx="9" cy="9" r="1" fill="currentColor"/></svg>,
  rates: <svg width="18" height="18" viewBox="0 0 18 18" fill="none"><path d="M3 14L14 3M5 5h.01M13 13h.01" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/><circle cx="5" cy="5" r="2" stroke="currentColor" strokeWidth="1.5"/><circle cx="13" cy="13" r="2" stroke="currentColor" strokeWidth="1.5"/></svg>,
  payments: <svg width="18" height="18" viewBox="0 0 18 18" fill="none"><rect x="1" y="3" width="16" height="12" rx="2" stroke="currentColor" strokeWidth="1.5"/><path d="M1 7h16" stroke="currentColor" strokeWidth="1.5"/><circle cx="9" cy="11" r="2" stroke="currentColor" strokeWidth="1.5"/></svg>,
  notifications: <svg width="18" height="18" viewBox="0 0 18 18" fill="none"><path d="M9 2a5 5 0 00-5 5v3l-1.5 2.5h13L14 10V7a5 5 0 00-5-5z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round"/><path d="M7 14.5a2 2 0 004 0" stroke="currentColor" strokeWidth="1.5"/></svg>,
  config: <svg width="18" height="18" viewBox="0 0 18 18" fill="none"><circle cx="9" cy="9" r="2.5" stroke="currentColor" strokeWidth="1.5"/><path d="M9 1v2M9 15v2M1 9h2M15 9h2M3.05 3.05l1.41 1.41M13.54 13.54l1.41 1.41M3.05 14.95l1.41-1.41M13.54 4.46l1.41-1.41" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/></svg>,
  finance: <svg width="18" height="18" viewBox="0 0 18 18" fill="none"><rect x="1" y="4" width="16" height="11" rx="2" stroke="currentColor" strokeWidth="1.5"/><path d="M1 7h16" stroke="currentColor" strokeWidth="1.5"/><path d="M5 11h2M9 11h4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/></svg>,
  users: <svg width="18" height="18" viewBox="0 0 18 18" fill="none"><circle cx="6.5" cy="6" r="3" stroke="currentColor" strokeWidth="1.5"/><path d="M1 16c0-3.038 2.462-5.5 5.5-5.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/><circle cx="13" cy="8" r="2.5" stroke="currentColor" strokeWidth="1.5"/><path d="M10 16c0-2.485 1.343-4.5 3-4.5s3 2.015 3 4.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/></svg>,
};

const Section = ({ collapsed, label, children, separatorTop }) => (
  <div>
    {separatorTop && <div className="my-2 mx-4 h-px bg-border" />}
    {!collapsed && label && (
      <div className="px-5 pt-3 pb-1.5 text-[10px] font-heading font-semibold uppercase tracking-[2px] text-muted-foreground/70">
        {label}
      </div>
    )}
    {children}
  </div>
);

const NavItem = ({ to, icon, label, badge, collapsed, active }) => (
  <Link
    to={to}
    className={`relative flex items-center gap-3 px-4 py-2.5 text-sm font-medium transition-colors group ${
      active
        ? 'text-primary bg-primary/10'
        : 'text-muted-foreground hover:text-foreground hover:bg-muted'
    }`}
  >
    {active && (
      <span className="absolute left-0 top-1/2 -translate-y-1/2 w-0.5 h-5 bg-primary rounded-r" />
    )}
    <span className="shrink-0 relative">{icon}</span>
    {!collapsed && <span className="flex-1 truncate">{label}</span>}
    {!collapsed && badge ? (
      <span className="ml-auto min-w-[20px] h-5 px-1.5 rounded-full bg-primary text-primary-foreground text-[10px] font-bold flex items-center justify-center">
        {badge > 99 ? '99+' : badge}
      </span>
    ) : null}
    {collapsed && (
      <span className="absolute left-full ml-2 px-2 py-1 rounded bg-card border border-border text-xs text-foreground whitespace-nowrap opacity-0 group-hover:opacity-100 pointer-events-none transition-opacity z-50 shadow-elevation-2">
        {label}
      </span>
    )}
  </Link>
);

/**
 * Renders one feature link only if the feature is ON for the current tenant.
 * Uses useFeature() hook directly — relies on TenantContext.
 */
const FeatureNavItem = ({ feature, collapsed, currentPath, unreadCount }) => {
  const enabled = useFeature(feature.slug);
  const { t } = useTranslation();
  if (!enabled) return null;
  const active = currentPath === feature.route;
  const label = t(`sidebar.features.${feature.slug}`, feature.label);
  // Notifications rules nav item gets the unread badge
  const badge = feature.slug === 'pages.notifications.rules' ? unreadCount : undefined;
  return (
    <NavItem
      to={feature.route}
      icon={Icon[feature.icon] || Icon.dashboard}
      label={label}
      badge={badge}
      collapsed={collapsed}
      active={active}
    />
  );
};

/**
 * Renders a section only if at least one of its features is ON for the current
 * tenant. Sections are derived from FEATURE_GROUPS and FEATURES_BY_GROUP, filtered
 * to admin-role features (this Sidebar is the admin backoffice nav).
 */
const FeatureSection = ({ groupId, collapsed, separatorTop, currentPath, unreadCount, features }) => {
  const { t } = useTranslation();
  const group = FEATURE_GROUPS.find((g) => g.id === groupId);
  if (!group) return null;
  // Hide the section title fallback to a translated key if available
  const label = t(`sidebar.groups.${groupId}`, group.label);
  return (
    <Section collapsed={collapsed} label={label} separatorTop={separatorTop}>
      {features.map((f) => (
        <FeatureNavItem
          key={f.slug}
          feature={f}
          collapsed={collapsed}
          currentPath={currentPath}
          unreadCount={unreadCount}
        />
      ))}
    </Section>
  );
};

const Sidebar = ({ collapsed, onToggle, userEmail }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { tenantName, agencyName, tenantSlug, features } = useTenant();
  // TCN tenant uses dedicated dark-theme app at /tcn/* — keep its focused
  // hand-crafted nav (Sprint 1 explicitly skips TCN: tenants à ne pas toucher).
  const isTcn = tenantSlug === 'tcn';
  const [unreadCount, setUnreadCount] = useState(0);

  useEffect(() => {
    let cancelled = false;
    let interval;
    const tick = async () => {
      // NOTE: NoiRock has no `notifications` table (Black OS legacy — TCN uses
      // `alert_events`). supabase-js returns { error } instead of throwing, so a
      // missing table would otherwise spam a 404 every 60s. Bail + stop polling
      // on any error until the notifications table is provisioned (see handoff).
      const { count, error } = await supabase
        .from('notifications')
        .select('id', { count: 'exact', head: true })
        .is('read_at', null);
      if (cancelled) return;
      if (error) {
        setUnreadCount(0);
        if (interval) clearInterval(interval);
        return;
      }
      setUnreadCount(count || 0);
    };
    tick();
    interval = setInterval(tick, 60000);
    return () => { cancelled = true; clearInterval(interval); };
  }, []);

  const handleLogout = async () => {
    await supabase.auth.signOut();
    navigate('/login-authentication', { replace: true });
  };

  const isActive = (path) => location.pathname === path;

  // For Generic branch : derive visible sections from the catalog (admin-side only).
  // A section is shown only if at least one of its features is in the catalog
  // AND has 'admin' in its roles list. The actual ON/OFF filtering happens per
  // FeatureNavItem via useFeature().
  const adminFeaturesByGroup = useMemo(() => {
    const out = {};
    for (const group of FEATURE_GROUPS) {
      const items = (FEATURES_BY_GROUP[group.id] || []).filter((f) =>
        // Skip parameterized detail routes (e.g. /admin/creators/:id) — they are
        // reached from their list page, not as standalone sidebar links.
        f.roles.includes('admin') && features?.[f.slug] && !f.route.includes(':')
      );
      if (items.length > 0) out[group.id] = items;
    }
    return out;
  }, [features]);

  return (
    <aside
      className="hidden md:flex flex-col h-screen sticky top-0 transition-all duration-300 border-r border-border bg-card shrink-0"
      style={{ width: collapsed ? 64 : 220 }}
    >
      {/* Logo + collapse */}
      <div className="flex items-center gap-3 px-4 py-4 border-b border-border" style={{ minHeight: 64 }}>
        <NoiRockLogo size={28} />
        {!collapsed && (
          <div className="leading-tight min-w-0 flex-1">
            <div className="font-heading font-bold text-foreground text-sm truncate">{tenantName || 'NoiRock'}</div>
            <div className="text-[10px] text-muted-foreground truncate">{agencyName || ''}</div>
          </div>
        )}
        <button
          onClick={onToggle}
          className="shrink-0 w-6 h-6 flex items-center justify-center rounded text-muted-foreground hover:text-foreground transition-colors"
          aria-label={collapsed ? t('admin.sidebar.expand', 'Expand') : t('admin.sidebar.collapse', 'Collapse')}
        >
          <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
            <path d={collapsed ? 'M5 3l4 4-4 4' : 'M9 3L5 7l4 4'} stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </button>
      </div>

      {/* Nav */}
      <nav className="flex-1 py-2 overflow-y-auto overflow-x-hidden">
        {isTcn ? (
          <>
            {/* ── TCN — focused Phase 1 nav (UNTOUCHED per Sprint 1 doctrine) ── */}
            <Section collapsed={collapsed} label={t('admin.sidebar.sections.tcn.overview', 'Overview')}>
              <NavItem to="/admin/dashboard" icon={Icon.dashboard} label={t('admin.sidebar.items.dashboard', 'Dashboard')} collapsed={collapsed} active={isActive('/admin/dashboard')} />
            </Section>

            <Section collapsed={collapsed} label={t('admin.sidebar.sections.tcn.ingestion', 'Ingestion')} separatorTop>
              <NavItem to="/tcn/admin/upload" icon={Icon.payments} label={t('admin.sidebar.items.upload', 'Upload Excel')} collapsed={collapsed} active={isActive('/tcn/admin/upload')} />
              <NavItem to="/tcn/admin/imports" icon={Icon.performance} label={t('admin.sidebar.items.imports', 'Import history')} collapsed={collapsed} active={isActive('/tcn/admin/imports')} />
            </Section>

            <Section collapsed={collapsed} label={t('admin.sidebar.sections.tcn.team', 'Team')} separatorTop>
              <NavItem to="/admin/users" icon={Icon.users} label={t('admin.sidebar.items.managers', 'Managers')} collapsed={collapsed} active={isActive('/admin/users')} />
              <NavItem to="/admin/creators" icon={Icon.creators} label={t('admin.sidebar.items.creators', 'Creators')} collapsed={collapsed} active={isActive('/admin/creators')} />
            </Section>

            <Section collapsed={collapsed} label={t('admin.sidebar.sections.tcn.rules', 'Rules & objectives')} separatorTop>
              <NavItem to="/admin/objective-rules" icon={Icon.objectives} label={t('admin.sidebar.items.objectiveRules', 'Objective rules')} collapsed={collapsed} active={isActive('/admin/objective-rules')} />
            </Section>
          </>
        ) : (
          <>
            {/* ── Generic — catalogue-driven nav (Sprint 1) ── */}
            {FEATURE_GROUPS.map((group, idx) => {
              const items = adminFeaturesByGroup[group.id];
              if (!items || items.length === 0) return null;
              return (
                <FeatureSection
                  key={group.id}
                  groupId={group.id}
                  collapsed={collapsed}
                  separatorTop={idx > 0}
                  currentPath={location.pathname}
                  unreadCount={unreadCount}
                  features={items}
                />
              );
            })}

            {/* Platform-level non-cataloged items (NoiRock-only, not from Black OS) */}
            <Section collapsed={collapsed} label={t('admin.sidebar.sections.platform', 'Platform')} separatorTop>
              <NavItem to="/admin/objective-rules" icon={Icon.objectives} label={t('admin.sidebar.items.objectiveRules', 'Objective rules')} collapsed={collapsed} active={isActive('/admin/objective-rules')} />
            </Section>
          </>
        )}
      </nav>

      {/* User + Logout */}
      <div className="border-t border-border p-3 shrink-0">
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate('/admin/profile')}
            className="shrink-0 w-8 h-8 rounded-full flex items-center justify-center text-primary-foreground text-xs font-bold bg-primary hover:ring-2 hover:ring-primary/40 transition-all"
            aria-label={t('admin.sidebar.profileTooltip', 'Profile')}
          >
            {(userEmail?.[0] || 'A').toUpperCase()}
          </button>
          {!collapsed && (
            <div className="flex-1 min-w-0">
              <p className="text-foreground text-xs font-medium truncate">{userEmail || 'Admin'}</p>
              <p className="text-muted-foreground text-[10px]">{t('admin.sidebar.role', 'Administrator')}</p>
            </div>
          )}
          <button
            onClick={handleLogout}
            className="shrink-0 w-7 h-7 flex items-center justify-center rounded text-muted-foreground hover:text-error transition-colors"
            aria-label={t('admin.sidebar.logout', 'Logout')}
            title={t('admin.sidebar.logout', 'Logout')}
          >
            <svg width="15" height="15" viewBox="0 0 15 15" fill="none">
              <path d="M6 2H3a1 1 0 00-1 1v9a1 1 0 001 1h3" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round"/>
              <path d="M10 10l3-2.5L10 5" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M13 7.5H6" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round"/>
            </svg>
          </button>
        </div>
      </div>
    </aside>
  );
};

export default Sidebar;
